import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../config/supabase';
import './calender.css';

const Calendar = () => {
  const { user } = useAuth();
  const [bookingDate, setBookingDate] = useState('');
  const [bookingType, setBookingType] = useState('consultation');
  const [notes, setNotes] = useState('');
  const [statusData, setStatusData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const intervalRef = useRef(null);

  useEffect(() => {
    if (user?.id) {
      fetchStatus();
    } else {
      setLoading(false);
      setError('Please login to view calendar bookings');
    }
  }, [user]);

  const fetchStatus = async () => {
    if (!user?.id) {
      setError('User not logged in');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { data, error: fetchError } = await supabase
        .from('calendar_requests')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      if (data && data.length > 0) {
        // 🔥 FIX: Find approved requests
        const approvedRequests = data.filter(req => req.status === 'approved');

        if (approvedRequests.length > 0) {
          // Prioritize the most recent approved request with a meeting_link
          const withLink = approvedRequests.filter(req => req.meeting_link);
          if (withLink.length > 0) {
            // Show the latest one with a link
            setStatusData(withLink[withLink.length - 1]);
          } else {
            // If none have a link, show the latest approved
            setStatusData(approvedRequests[approvedRequests.length - 1]);
          }
        } else {
          // If no approved, show the latest overall (pending/rejected)
          setStatusData(data[data.length - 1]);
        }
      } else {
        setStatusData(null);
      }
    } catch (err) {
      console.error('Status fetch error', err);
      setError(err.message || 'Failed to load status');
    } finally {
      setLoading(false);
    }
  };

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (user?.id) {
      intervalRef.current = setInterval(fetchStatus, 30000);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user?.id) {
      alert('Please login first');
      return;
    }

    try {
      const { error: insertError } = await supabase
        .from('calendar_requests')
        .insert([{
          user_id: user.id,
          booking_date: bookingDate,
          booking_type: bookingType,
          notes: notes,
          status: 'pending'
        }]);

      if (insertError) throw insertError;

      alert('Request submitted successfully');
      setBookingDate('');
      setNotes('');
      fetchStatus();
    } catch (err) {
      console.error('Submit error', err);
      alert('Failed to submit request');
    }
  };

  /* ================= UI ================= */
  return (
    <div className="calendar-container">
      <div className="calendar-header-section">
        <h1>Expert Session Request</h1>
        <p className="calendar-subtitle">
          Submit your request. We will notify you once approved.
        </p>
      </div>

      {error && <p className="error-text">{error}</p>}

      {!loading && statusData && (
        <div className="status-card">
          <h3>Status: {statusData.status.toUpperCase()}</h3>

          {statusData.status === 'pending' && (
            <p>⏳ Your request is under review. We’ll update you soon.</p>
          )}

          {statusData.status === 'approved' && (
            <div>
              <p>✅ Your session is approved</p>
              <p><b>Date:</b> {statusData.booking_date}</p>

              {statusData.session_time && (
                <p><b>Time:</b> {statusData.session_time}</p>
              )}

              {statusData.meeting_link && (
                <p>
                  <b>Join Link:</b>{' '}
                  <a
                    href={statusData.meeting_link}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Join Session
                  </a>
                </p>
              )}
            </div>
          )}

          {statusData.status === 'rejected' && (
            <p>❌ Unfortunately your request was rejected.</p>
          )}
        </div>
      )}

      {!statusData || statusData.status === 'rejected' ? (
        <form className="booking-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Select Date</label>
            <input
              type="date"
              required
              value={bookingDate}
              onChange={(e) => setBookingDate(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Session Type</label>
            <select
              value={bookingType}
              onChange={(e) => setBookingType(e.target.value)}
            >
              <option value="consultation">1:1 Expert Consultation</option>
              <option value="regulatory">Regulatory Advisory</option>
              <option value="technical">Technical Review</option>
            </select>
          </div>

          <div className="form-group">
            <label>Notes</label>
            <textarea
              rows="3"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          <button className="btn-confirm" type="submit">
            Submit Request
          </button>
        </form>
      ) : null}
    </div>
  );
};

export default Calendar;
